import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { api } from '../../api';
import { createSessionEventSource } from '../../services/realtimeService';

const emptyBootstrap = {
  adminDraftQuiz: null,
  questionSets: [],
  activeGameSession: null,
  wrongDelayOptions: [1, 2, 3, 5, 10]
};

const emptyLiveStatus = {
  gameStatus: 'idle',
  phase: 'idle',
  currentRoundIndex: 0,
  currentRoundTitle: null,
  activeQuizName: '',
  enabledPlayersCount: 0,
  joinedPlayersCount: 0,
  finishedPlayersCount: 0,
  waitingFor: [],
  players: []
};

const statusLabels = {
  idle: 'Nije pokrenuta',
  running: 'Pokrenuta',
  stopped: 'Zaustavljena',
  completed: 'Završena'
};

const phaseLabels = {
  idle: 'idle',
  playing: 'playing',
  roundComplete: 'roundComplete',
  unlock: 'unlock',
  finished: 'finished',
  stopped: 'stopped'
};

const toneColor = {
  success: 'success',
  info: 'info',
  warning: 'warning',
  error: 'error',
  default: 'default'
};

function formatClock(timestamp) {
  return new Date(timestamp).toLocaleTimeString('hr-HR', { hour: '2-digit', minute: '2-digit' });
}

export function GameSettingsPage() {
  const [bootstrap, setBootstrap] = useState(emptyBootstrap);
  const [quiz, setQuiz] = useState(null);
  const [liveStatus, setLiveStatus] = useState(emptyLiveStatus);
  const [activityLog, setActivityLog] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, severity: 'success', message: '' });
  const [lastSaveAction, setLastSaveAction] = useState(null);
  const [lastRuntimeAction, setLastRuntimeAction] = useState(null);
  const [pendingAction, setPendingAction] = useState('');
  const [selectedSavedQuizId, setSelectedSavedQuizId] = useState('');

  const showFeedback = useCallback((severity, message, scope = 'runtime') => {
    const timestamp = new Date();
    setSnackbar({ open: true, severity, message });
    const nextAction = { severity, message, timestamp: formatClock(timestamp.toISOString()) };
    if (scope === 'save') {
      setLastSaveAction(nextAction);
      return;
    }
    setLastRuntimeAction(nextAction);
  }, []);

  const loadBootstrap = useCallback(async () => {
    const data = await api.getAdminBootstrap();
    setBootstrap(data);
    setQuiz(data.adminDraftQuiz);
    setSelectedSavedQuizId((current) => (data.savedQuizzes.some((savedQuiz) => savedQuiz.id === current) ? current : ''));
  }, []);

  const loadLiveStatus = useCallback(async () => {
    const [statusResponse, logResponse] = await Promise.all([
      api.getAdminGameStatus(),
      api.getAdminGameLog()
    ]);
    setLiveStatus(statusResponse.data || emptyLiveStatus);
    setActivityLog(logResponse.data?.events || []);
  }, []);

  useEffect(() => {
    loadBootstrap();
    loadLiveStatus();
  }, [loadBootstrap, loadLiveStatus]);

  useEffect(() => {
    const source = createSessionEventSource();
    const refresh = () => {
      loadLiveStatus().catch(() => {});
    };
    ['session:update', 'player:joined', 'player:progress', 'round:completed', 'round:unlocked', 'game:finished', 'game:stopped', 'admin:update']
      .forEach((eventName) => source.addEventListener(eventName, refresh));
    source.onerror = () => {};

    const interval = setInterval(() => {
      loadLiveStatus().catch(() => {});
    }, 5000);

    return () => {
      clearInterval(interval);
      source.close();
    };
  }, [loadLiveStatus]);

  const questionSetOptions = useMemo(() => bootstrap.questionSets || [], [bootstrap.questionSets]);

  const updateQuiz = (changes) => setQuiz((current) => ({ ...current, ...changes }));
  const updateRound = (roundId, changes) => setQuiz((current) => ({
    ...current,
    rounds: current.rounds.map((round) => (round.id === roundId ? { ...round, ...changes } : round))
  }));

  const runAction = async (actionKey, executor, options = {}) => {
    const { genericError = 'Akcija nije uspjela.', successSeverity = 'success', scope = 'runtime' } = options;
    setPendingAction(actionKey);
    try {
      const result = await executor();
      if (options.afterSuccess) {
        await options.afterSuccess(result);
      }
      showFeedback(successSeverity, result?.message || options.successMessage || 'Akcija je uspješno izvršena.', scope);
      return result;
    } catch (error) {
      showFeedback(error.code === 'INSUFFICIENT_QUESTIONS' ? 'warning' : 'error', error.message || genericError, scope);
      return null;
    } finally {
      setPendingAction('');
    }
  };

  if (!quiz) return null;

  const waitingForText = liveStatus.waitingFor?.length
    ? liveStatus.waitingFor.map((player) => player.name ? `${player.playerLabel} (${player.name})` : player.playerLabel).join(', ')
    : 'Svi natjecatelji su završili trenutni krug.';

  const solvingPlayers = liveStatus.players.filter((player) => player.joined && !player.roundDone && player.tone === 'info');
  const finishedPlayers = liveStatus.players.filter((player) => player.roundDone);

  return (
    <Container>
      <Stack spacing={3}>
        <Typography variant="h4">Postavke igre</Typography>

        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="h6">Osnovne postavke</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Ime kviza"
                    helperText="Ime kviza koristi se za spremanje i kasnije ponovno učitavanje kviza."
                    value={quiz.internalName}
                    onChange={(event) => updateQuiz({ internalName: event.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Naslov kviza" value={quiz.publicTitle} onChange={(event) => updateQuiz({ publicTitle: event.target.value })} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth multiline minRows={3} label="Uvodni tekst" value={quiz.introText} onChange={(event) => updateQuiz({ introText: event.target.value })} />
                </Grid>
              </Grid>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexWrap="wrap">
                <FormControlLabel control={<Checkbox checked={quiz.showLogo} onChange={(event) => updateQuiz({ showLogo: event.target.checked })} />} label="Prikaži logotip" />
                <FormControlLabel control={<Checkbox checked={quiz.enableCorrectSound} onChange={(event) => updateQuiz({ enableCorrectSound: event.target.checked })} />} label="Zvuk točnog odgovora" />
                <FormControlLabel control={<Checkbox checked={quiz.enableWrongSound} onChange={(event) => updateQuiz({ enableWrongSound: event.target.checked })} />} label="Zvuk netočnog odgovora" />
                <FormControlLabel control={<Checkbox checked={quiz.enableWrongAnswerDelay} onChange={(event) => updateQuiz({ enableWrongAnswerDelay: event.target.checked })} />} label="Pauza nakon netočnog odgovora" />
              </Stack>
              <Box sx={{ maxWidth: 240 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>Dužina pauze</Typography>
                <Select
                  fullWidth
                  value={quiz.wrongAnswerDelaySeconds}
                  disabled={!quiz.enableWrongAnswerDelay}
                  onChange={(event) => updateQuiz({ wrongAnswerDelaySeconds: Number(event.target.value) })}
                >
                  {bootstrap.wrongDelayOptions.map((option) => (
                    <MenuItem key={option} value={option}>{option} sekundi</MenuItem>
                  ))}
                </Select>
              </Box>
              <Typography variant="subtitle1">Natjecatelji</Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap">
                {[1, 2, 3, 4, 5, 6].map((playerNo) => (
                  <FormControlLabel
                    key={playerNo}
                    control={
                      <Checkbox
                        checked={quiz.enabledPlayers.includes(playerNo)}
                        onChange={(event) => {
                          const enabledPlayers = event.target.checked
                            ? [...quiz.enabledPlayers, playerNo]
                            : quiz.enabledPlayers.filter((value) => value !== playerNo);
                          updateQuiz({ enabledPlayers: enabledPlayers.sort((a, b) => a - b) });
                        }}
                      />
                    }
                    label={`Natjecatelj ${playerNo}`}
                  />
                ))}
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">Krugovi</Typography>
                <Button
                  variant="outlined"
                  onClick={() => updateQuiz({
                    rounds: [...quiz.rounds, {
                      id: `round-${Date.now()}-${Math.random().toString(16).slice(2)}`,
                      title: `Krug ${quiz.rounds.length + 1}`,
                      questionSetId: questionSetOptions[0]?.id || null,
                      questionsPerPlayer: 5,
                      secretWord: '',
                      completionMessage: 'Čestitamo! Završili ste krug.'
                    }]
                  })}
                >
                  Dodaj krug
                </Button>
              </Stack>
              {quiz.rounds.map((round, index) => (
                <Card key={round.id} variant="outlined">
                  <CardContent>
                    <Stack spacing={2}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="subtitle1">Krug {index + 1}</Typography>
                        {quiz.rounds.length > 1 && (
                          <Button color="error" onClick={() => updateQuiz({ rounds: quiz.rounds.filter((item) => item.id !== round.id) })}>
                            Ukloni krug
                          </Button>
                        )}
                      </Stack>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <TextField fullWidth label="Naslov kruga" value={round.title} onChange={(event) => updateRound(round.id, { title: event.target.value })} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" sx={{ mb: 1 }}>Set pitanja</Typography>
                          <Select fullWidth value={round.questionSetId || ''} onChange={(event) => updateRound(round.id, { questionSetId: event.target.value })}>
                            {questionSetOptions.map((set) => (
                              <MenuItem key={set.id} value={set.id}>{set.name} ({set.questionCount})</MenuItem>
                            ))}
                          </Select>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            type="number"
                            label="Broj pitanja po igraču"
                            value={round.questionsPerPlayer}
                            onChange={(event) => updateRound(round.id, { questionsPerPlayer: Number(event.target.value) })}
                          />
                        </Grid>
                        <Grid item xs={12} md={8}>
                          <TextField
                            fullWidth
                            label={index === quiz.rounds.length - 1 ? 'Tajna riječ (nije potrebna za zadnji krug)' : 'Tajna riječ za otključavanje'}
                            value={round.secretWord}
                            onChange={(event) => updateRound(round.id, { secretWord: event.target.value })}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            multiline
                            minRows={2}
                            label="Poruka nakon završetka kruga"
                            value={round.completionMessage}
                            onChange={(event) => updateRound(round.id, { completionMessage: event.target.value })}
                          />
                        </Grid>
                      </Grid>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="h6">Spremanje postavki</Typography>
              <Typography color="text.secondary">
                Spremite konfiguraciju kviza prije pokretanja igre ili nakon izmjena u formi.
              </Typography>
              {quiz.id && (
                <Alert severity="info">
                  Uređujete kviz: {quiz.internalName}
                </Alert>
              )}
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <Stack spacing={1} flex={1}>
                  <Button
                    variant="contained"
                    disabled={!!pendingAction}
                    onClick={() => runAction('saveDraft', () => api.updateDraftQuiz(quiz), {
                      successMessage: 'Trenutne postavke igre su spremljene.',
                      genericError: 'Spremanje postavki nije uspjelo. Pokušajte ponovno.',
                      scope: 'save',
                      afterSuccess: async (result) => {
                        setQuiz(result.data);
                        await loadBootstrap();
                        await loadLiveStatus();
                      }
                    })}
                  >
                    {pendingAction === 'saveDraft' ? 'Spremanje...' : 'Spremi trenutne postavke'}
                  </Button>
                  <Typography variant="body2" color="text.secondary">
                    Sprema trenutno uređene postavke igre.
                  </Typography>
                </Stack>
                <Stack spacing={1} flex={1}>
                  <Button
                    variant="contained"
                    color="secondary"
                    disabled={!!pendingAction}
                    onClick={() => {
                      if (!String(quiz.internalName || '').trim()) {
                        showFeedback('warning', 'Unesite ime kviza prije spremanja kviza.', 'save');
                        return;
                      }
                      runAction('saveQuiz', () => (quiz.id ? api.updateQuiz(quiz.id, quiz) : api.saveQuiz(quiz)), {
                        successMessage: `Kviz "${quiz.internalName}" je spremljen i dostupan u sekciji Kvizovi.`,
                        genericError: 'Spremanje kviza nije uspjelo.',
                        scope: 'save',
                        afterSuccess: async (result) => {
                          setQuiz(result.data);
                          await loadBootstrap();
                          await loadLiveStatus();
                        }
                      });
                    }}
                  >
                    {pendingAction === 'saveQuiz' ? 'Spremanje...' : 'Spremi kao kviz'}
                  </Button>
                  <Typography variant="body2" color="text.secondary">
                    Sprema ove postavke kao kviz koji kasnije možete učitati.
                  </Typography>
                </Stack>
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
                <Select
                  displayEmpty
                  fullWidth
                  value={selectedSavedQuizId}
                  onChange={(event) => setSelectedSavedQuizId(event.target.value)}
                >
                  <MenuItem value="">Odaberite spremljeni kviz</MenuItem>
                  {bootstrap.savedQuizzes.map((savedQuiz) => (
                    <MenuItem key={savedQuiz.id} value={savedQuiz.id}>{savedQuiz.internalName}</MenuItem>
                  ))}
                </Select>
                <Button
                  variant="outlined"
                  disabled={!!pendingAction || !selectedSavedQuizId}
                  onClick={() => runAction('loadQuiz', () => api.loadQuizToDraft(selectedSavedQuizId), {
                    scope: 'save',
                    genericError: 'Učitavanje kviza nije uspjelo.',
                    afterSuccess: async (result) => {
                      setQuiz(result.data);
                      await loadBootstrap();
                      await loadLiveStatus();
                    }
                  })}
                >
                  {pendingAction === 'loadQuiz' ? 'Učitavanje...' : 'Učitaj kviz'}
                </Button>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Učitava već spremljeni kviz u trenutne postavke igre.
              </Typography>
              {lastSaveAction && (
                <Alert severity={lastSaveAction.severity}>
                  Zadnja akcija spremanja: {lastSaveAction.message} ({lastSaveAction.timestamp})
                </Alert>
              )}
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="h6">Kontrole igre</Typography>
              <Typography color="text.secondary">
                Status igre: {statusLabels[liveStatus.gameStatus] || liveStatus.gameStatus} · Faza: {phaseLabels[liveStatus.phase] || liveStatus.phase}
              </Typography>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <Button
                  variant="contained"
                  color="success"
                  disabled={!!pendingAction}
                  onClick={() => runAction('start', async () => {
                    await api.updateDraftQuiz(quiz);
                    return api.startGame();
                  }, {
                    successMessage: 'Igra je uspješno pokrenuta.',
                    genericError: 'Pokretanje igre nije uspjelo.',
                    scope: 'runtime',
                    afterSuccess: async () => {
                      await loadBootstrap();
                      await loadLiveStatus();
                    }
                  })}
                >
                  {pendingAction === 'start' ? 'Pokretanje...' : 'Pokreni igru'}
                </Button>
                <Button
                  variant="contained"
                  color="warning"
                  disabled={!!pendingAction}
                  onClick={() => runAction('reset', () => api.resetGame(), {
                    successMessage: 'Igra je vraćena na početno stanje.',
                    genericError: 'Reset igre nije uspio.',
                    scope: 'runtime',
                    afterSuccess: async () => {
                      await loadBootstrap();
                      await loadLiveStatus();
                    }
                  })}
                >
                  {pendingAction === 'reset' ? 'Resetiranje...' : 'Reset'}
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  disabled={!!pendingAction}
                  onClick={() => runAction('stop', () => api.stopGame(), {
                    successMessage: 'Igra je zaustavljena.',
                    genericError: 'Zaustavljanje igre nije uspjelo.',
                    scope: 'runtime',
                    afterSuccess: async () => {
                      await loadBootstrap();
                      await loadLiveStatus();
                    }
                  })}
                >
                  {pendingAction === 'stop' ? 'Zaustavljanje...' : 'Zaustavi igru'}
                </Button>
              </Stack>
              {lastRuntimeAction && (
                <Alert severity={lastRuntimeAction.severity}>
                  Zadnja akcija: {lastRuntimeAction.message} ({lastRuntimeAction.timestamp})
                </Alert>
              )}
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Stack spacing={3}>
              <Typography variant="h6">Status i log igre</Typography>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexWrap="wrap">
                <Chip label={`Status igre: ${statusLabels[liveStatus.gameStatus] || liveStatus.gameStatus}`} color="primary" variant="outlined" />
                <Chip label={`Trenutni krug: ${liveStatus.currentRoundTitle || (liveStatus.gameStatus === 'idle' ? '—' : `Krug ${liveStatus.currentRoundIndex + 1}`)}`} variant="outlined" />
                <Chip label={`Faza: ${phaseLabels[liveStatus.phase] || liveStatus.phase}`} variant="outlined" />
                <Chip label={`Aktivni kviz: ${liveStatus.activeQuizName || quiz.internalName || '—'}`} variant="outlined" />
                <Chip label={`Uključeni natjecatelji: ${liveStatus.enabledPlayersCount}`} variant="outlined" />
                <Chip label={`Prijavljeni natjecatelji: ${liveStatus.joinedPlayersCount}/${liveStatus.enabledPlayersCount}`} variant="outlined" />
              </Stack>

              <Divider />

              <Stack spacing={1}>
                <Typography variant="subtitle1">Trenutni status</Typography>
                <Typography color="text.secondary">
                  Trenutno rješavaju: {solvingPlayers.length ? solvingPlayers.map((player) => player.name ? `${player.playerLabel} (${player.name})` : player.playerLabel).join(', ') : 'nitko'}
                </Typography>
                <Typography color="text.secondary">
                  Završili trenutni krug: {finishedPlayers.length ? finishedPlayers.map((player) => player.name ? `${player.playerLabel} (${player.name})` : player.playerLabel).join(', ') : 'nitko'}
                </Typography>
              </Stack>

              <Divider />

              <Stack spacing={1}>
                <Typography variant="subtitle1">Status natjecatelja</Typography>
                <List dense>
                  {liveStatus.players.map((player) => (
                    <ListItem key={player.playerNo} divider>
                      <ListItemText
                        primary={`${player.playerLabel}${player.name ? ` — ${player.name}` : ''}`}
                        secondary={player.statusText}
                      />
                      <Chip size="small" color={toneColor[player.tone] || 'default'} label={player.roundDone ? 'završio' : player.joined ? 'aktivan' : 'nije prijavljen'} />
                    </ListItem>
                  ))}
                </List>
              </Stack>

              <Divider />

              <Stack spacing={1}>
                <Typography variant="subtitle1">Čeka se</Typography>
                <Alert severity={liveStatus.waitingFor?.length ? 'warning' : 'success'}>
                  {liveStatus.waitingFor?.length ? `Čeka se: ${waitingForText}` : waitingForText}
                </Alert>
              </Stack>

              <Divider />

              <Stack spacing={1}>
                <Typography variant="subtitle1">Dnevnik aktivnosti</Typography>
                <Box sx={{ maxHeight: 320, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <List dense>
                    {activityLog.map((event) => (
                      <ListItem key={event.id} divider>
                        <ListItemText
                          primary={`${formatClock(event.timestamp)} — ${event.message}`}
                          secondary={event.type}
                        />
                      </ListItem>
                    ))}
                    {!activityLog.length && (
                      <ListItem>
                        <ListItemText primary="Još nema događaja za prikaz." />
                      </ListItem>
                    )}
                  </List>
                </Box>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar((current) => ({ ...current, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
